'use client';

import { Box, VStack } from '@terra/ui-styled-system';
import { DEFAULT_TARGET_REQUESTS_PER_MINUTE } from '@terra/shared-utils';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Slider } from '../../../components/slider';
import { SettingsRow } from '../SettingsRow';

const SLIDER_MIN = 0;
const SLIDER_MAX = 6000;
const SLIDER_STEP = 10;

export type RequestsPerMinuteSliderProps = {
  label: string;
  tooltip: string;
  value: number | null | undefined;
  disabled?: boolean;
  onCommit: (next: number | null) => boolean | Promise<boolean>;
};

const toSliderValue = (rpm: number | null | undefined): number => {
  if (rpm == null) {
    return DEFAULT_TARGET_REQUESTS_PER_MINUTE;
  }

  return Math.min(SLIDER_MAX, Math.max(SLIDER_MIN, rpm));
};

const toRpm = (sliderValue: number): number | null => {
  if (sliderValue <= SLIDER_MIN) {
    return null;
  }

  return Math.min(SLIDER_MAX, Math.max(1, sliderValue));
};

export const RequestsPerMinuteSlider = ({
  label,
  tooltip,
  value,
  disabled,
  onCommit,
}: RequestsPerMinuteSliderProps) => {
  const tCommon = useTranslations('designSystem.common');
  const [draft, setDraft] = useState(() => toSliderValue(value));

  useEffect(() => {
    setDraft(toSliderValue(value));
  }, [value]);

  const displayValue = draft <= SLIDER_MIN ? tCommon('na') : String(draft);

  return (
    <VStack alignItems="stretch" gap={2} w="full">
      <SettingsRow label={label} tooltip={tooltip} value={displayValue} />
      <Box px={1}>
        <Slider
          size="sm"
          min={SLIDER_MIN}
          max={SLIDER_MAX}
          step={SLIDER_STEP}
          value={[draft]}
          disabled={disabled}
          thumbAriaLabels={[label]}
          onValueChange={(details) => {
            const next = details.value[0];

            if (next == null) {
              return;
            }

            setDraft(next);
          }}
          onValueChangeEnd={(details) => {
            const next = details.value[0] ?? draft;
            const rpm = toRpm(next);

            setDraft(next);

            if (rpm === (value ?? null)) {
              return;
            }

            void Promise.resolve(onCommit(rpm)).then((didPersist) => {
              if (didPersist === false) {
                setDraft(toSliderValue(value));
              }
            });
          }}
        />
      </Box>
    </VStack>
  );
};
